import {
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  Stack,
  Switch,
  ToastId,
} from "@chakra-ui/react";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  useEncryptVrStreamSettingQuery,
  useNotificationToast,
  useOrganizationCloudRenderingPolicies,
} from "../hooks";
import { UserPreferences } from "../hooks/types";
import { useUpdateUserPreferencesMutation } from "../hooks/useUpdateUserPreferencesMutation";
import { useUseXRGatewaySettingQuery } from "../hooks/useUseXRGatewaySettingQuery";
import { Policy } from "../session-management-api";

const nonEnforcedPolicies: (Policy | undefined)[] = [
  "OptIn",
  "OptOut",
  undefined,
];

let activeToastId: ToastId | undefined;
export function StreamingSecurityPreferences() {
  const { t } = useTranslation();
  const toast = useNotificationToast();
  const { data: policies, isPending: arePoliciesPending } =
    useOrganizationCloudRenderingPolicies();
  const canUserChangeEncryptionSetting = nonEnforcedPolicies.includes(
    policies?.cloudXREncryption,
  );
  const canUserChangeXRGatewaySetting = nonEnforcedPolicies.includes(
    policies?.xrGatewayPolicy,
  );
  const encryptVrStreamSettingQuery = useEncryptVrStreamSettingQuery();
  const useXrGatewaySettingQuery = useUseXRGatewaySettingQuery();
  const updateUserPreferencesMutation = useUpdateUserPreferencesMutation();
  const updateUserPreferences = useCallback(
    (
      preferences: Pick<UserPreferences, "encryptVrStream" | "useXRGateway">,
    ) => {
      updateUserPreferencesMutation.mutate(preferences, {
        onSuccess: () => {
          activeToastId && toast.close(activeToastId);
          activeToastId = toast({
            title: t("streamingPreferences.dialog.savedTitle"),
            description: t("streamingPreferences.dialog.savedSecurity"),
            status: "success",
          });
        },
        onError: () => {
          activeToastId && toast.close(activeToastId);
          activeToastId = toast({
            title: t("streamingPreferences.dialog.errorTitle"),
            description: t("streamingPreferences.dialog.error"),
            status: "error",
          });
        },
      });
    },
    [toast, t, updateUserPreferencesMutation],
  );

  return (
    <Stack spacing={4}>
      <FormControl
        as="fieldset"
        isDisabled={
          arePoliciesPending ||
          !canUserChangeEncryptionSetting ||
          updateUserPreferencesMutation.isPending ||
          encryptVrStreamSettingQuery.isPending
        }
      >
        <HStack>
          <Switch
            id="encrypt-vr-stream"
            isChecked={encryptVrStreamSettingQuery.data}
            onChange={(evt) =>
              updateUserPreferences({
                encryptVrStream: evt.target.checked,
                // If the user disables encryption, we also disable the XR Gateway
                useXRGateway: evt.target.checked ? undefined : false,
              })
            }
          />
          <FormLabel htmlFor="encrypt-vr-stream" margin={0}>
            {t("streamingPreferences.security.encrypt_vr_stream")}
          </FormLabel>
        </HStack>
        <FormHelperText marginTop={2}>
          {canUserChangeEncryptionSetting
            ? encryptVrStreamSettingQuery.data === false
              ? t(
                  "streamingPreferences.security.encrypt_vr_stream_help_text_disabled",
                )
              : t(
                  "streamingPreferences.security.encrypt_vr_stream_help_text_enabled",
                )
            : t(
                "streamingPreferences.security.policies_deny_encrypt_vr_stream_change",
              )}
        </FormHelperText>
      </FormControl>
      <FormControl
        as="fieldset"
        isDisabled={
          arePoliciesPending ||
          !canUserChangeXRGatewaySetting ||
          updateUserPreferencesMutation.isPending ||
          encryptVrStreamSettingQuery.data === false ||
          encryptVrStreamSettingQuery.isPending ||
          useXrGatewaySettingQuery.isPending
        }
        borderLeft={"1px"}
        borderColor={"gray.200"}
        paddingLeft={5}
      >
        <Stack>
          <HStack>
            <Switch
              id="use-xr-gateway"
              isChecked={useXrGatewaySettingQuery.data}
              onChange={(evt) =>
                updateUserPreferences({
                  useXRGateway: evt.target.checked,
                  encryptVrStream: encryptVrStreamSettingQuery.data,
                })
              }
            />
            <FormLabel htmlFor="use-xr-gateway" margin={0}>
              {t("streamingPreferences.protocols.use_xr_gateway")}
            </FormLabel>
          </HStack>
        </Stack>
        <FormHelperText marginTop={2}>
          {encryptVrStreamSettingQuery.data === false
            ? t(
                "streamingPreferences.protocols.use_xr_gateway_help_encryption_required",
              )
            : t(
                "streamingPreferences.protocols.use_xr_gateway_help_text_disabled",
              )}
        </FormHelperText>
      </FormControl>
    </Stack>
  );
}
